import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const Wrap = styled.div`
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--bg);
  padding: 12px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 80px;
  gap: 10px;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px dashed var(--border);
  &:last-child { border-bottom: none; }
`;

const SaveBar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
`;

const Primary = styled.button`
  padding: 10px 14px;
  border-radius: 20px;
  border: none;
  background: var(--accent);
  color: var(--accent-contrast);
  font-weight: 700;
  cursor: pointer;
`;

export default function FeaturedEditor({ token }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/admin/products', { headers: { Authorization: 'Bearer ' + token } });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Failed to load products');
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load products');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const updateField = (id, key, value) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [key]: value } : r)));
  };

  const save = async () => {
    try {
      setSaving(true);
      const payload = rows.map((r) => ({ id: r.id, featured: !!r.featured, featured_rank: r.featured_rank == null || r.featured_rank === '' ? null : Number(r.featured_rank) }));
      const res = await fetch('/api/admin/featured', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ updates: payload }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Save failed');
    } catch (e) {
      alert(e.message || 'Failed to save featured settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ color: 'var(--muted)' }}>Loading…</div>;
  if (error) return <div style={{ color: '#b91c1c' }}>{error}</div>;

  const byRank = [...rows].sort((a, b) => {
    const ra = a.featured_rank ?? 9999;
    const rb = b.featured_rank ?? 9999;
    return ra - rb || String(a.name).localeCompare(String(b.name));
  });

  return (
    <Wrap>
      <Row style={{ color: 'var(--muted)', fontWeight: 700 }}>
        <div>Name</div>
        <div>Featured</div>
        <div>Rank</div>
        <div></div>
      </Row>
      {byRank.map((p) => (
        <Row key={p.id}>
          <div>{p.name}</div>
          <div>
            <input type="checkbox" checked={!!p.featured} onChange={(e) => updateField(p.id, 'featured', e.target.checked)} />
          </div>
          <div>
            <input
              type="number"
              value={p.featured_rank ?? ''}
              onChange={(e) => updateField(p.id, 'featured_rank', e.target.value === '' ? '' : Number(e.target.value))}
              style={{ width: 100, padding: 6, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)' }}
            />
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>{p.slug}</div>
        </Row>
      ))}
      <SaveBar>
        <Primary onClick={save} disabled={saving}>Save Featured</Primary>
      </SaveBar>
    </Wrap>
  );
}

